/**
 * The eight demonstrations, as INLINE animated SVG (SPEC_CAPTURE_UX_R2 §4).
 *
 * Deliberate choices, each one a spec line or a constraint of this audience:
 *   · inline SVG, no Lottie runtime, no fetched asset — a mid-range Android on a 2G tail cannot afford either, and
 *     the whole set is a few KB of markup inside the bundle it already downloaded;
 *   · ONE drawing per demonstration, with ONE moving group whose three poses ARE the three static frames: the
 *     `prefers-reduced-motion` / low-data fallback shows the same art, never a second set of pictures to keep
 *     in step (the motion table below is the single source of both);
 *   · ink line-art on `currentColor` (the design system's text ink) with at most ONE accent, `--sv-showme-accent`,
 *     which the stylesheet maps to the calm information blue — never amber: amber is action, and a demonstration
 *     is not a control (§4);
 *   · gender-neutral, culturally unmarked silhouettes: a circle head, a plain torso, no hair, no clothing detail,
 *     no religious or regional marker, no real premises and no real face (the stamp preview's rule);
 *   · NO text inside the art — not even a `<title>`: the caption comes from the i18n deck so it is translated and
 *     the DEC-073 vocabulary gate can read it.
 */

import type { ReactNode } from 'react';
import { type DemoType } from './demoTypes';

/** How the moving group moves, and — the same values — where it sits in each of the three static frames. */
export interface DemoMotion {
  readonly kind: 'translate' | 'rotate' | 'scale';
  /** Exactly three poses: SMIL `values` and the three frames of the fallback. */
  readonly poses: readonly [string, string, string];
  readonly dur: string;
}

export const DEMO_MOTION: Readonly<Record<DemoType, DemoMotion>> = {
  STREET: { kind: 'translate', poses: ['0 0', '9 0', '17 0'], dur: '2.6s' },
  SIDEWAYS: { kind: 'rotate', poses: ['0 60 46', '-45 60 46', '-90 60 46'], dur: '2.4s' },
  DOOR: { kind: 'translate', poses: ['0 0', '11 0', '21 0'], dur: '2.6s' },
  TURN: { kind: 'rotate', poses: ['0 60 46', '180 60 46', '360 60 46'], dur: '3.2s' },
  PLATE: { kind: 'scale', poses: ['0.62', '0.81', '1'], dur: '2.4s' },
  CERTIFICATES: { kind: 'translate', poses: ['-26 0', '0 0', '26 0'], dur: '2.8s' },
  FACE: { kind: 'rotate', poses: ['-16 60 40', '-8 60 40', '0 60 40'], dur: '2.4s' },
  ID_FLAT: { kind: 'rotate', poses: ['-17 60 47', '-9 60 47', '0 60 47'], dur: '2.4s' },
};

const S = { stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

/**
 * The moving group: animated (SMIL) when `frame` is null, parked on pose `frame` otherwise. SMIL rather than CSS
 * keyframes for one reason — the poses can be the SAME literals the static frames use, so motion and fallback can
 * never drift.
 */
function Moving({ type, frame, children }: { type: DemoType; frame: number | null; children: ReactNode }): JSX.Element {
  const m = DEMO_MOTION[type];
  const pose = m.poses[Math.min(Math.max(frame ?? 0, 0), 2)] as string;
  const fn = m.kind === 'translate' ? `translate(${pose})` : m.kind === 'rotate' ? `rotate(${pose})` : `scale(${pose})`;
  return (
    <g className="mv" transform={fn}>
      {frame === null ? (
        <animateTransform
          attributeName="transform"
          type={m.kind}
          values={m.poses.join('; ')}
          keyTimes="0; 0.5; 1"
          dur={m.dur}
          repeatCount="indefinite"
          calcMode="spline"
          keySplines="0.4 0 0.2 1; 0.4 0 0.2 1"
        />
      ) : null}
      {children}
    </g>
  );
}

/** A person, front-on: circle head, plain torso, two legs. No face, no hair, no clothing detail. */
function Person({ x, y, phone = false }: { x: number; y: number; phone?: boolean }): JSX.Element {
  return (
    <g transform={`translate(${x} ${y})`}>
      <circle cx="0" cy="-22" r="5" {...S} strokeWidth="2" fill="none" />
      <path d="M0 -17v13" {...S} strokeWidth="2" />
      <path d="M-6 -12h12" {...S} strokeWidth="2" />
      <path d="M0 -4l-5 10M0 -4l5 10" {...S} strokeWidth="2" />
      {phone ? (
        <g className="ac">
          <rect x="-4.5" y="-14" width="9" height="12" rx="1.6" {...S} strokeWidth="1.8" fill="none" />
          <path d="M-6 -12l-3 4M6 -12l3 4" {...S} strokeWidth="1.8" />
        </g>
      ) : null}
    </g>
  );
}

/** A phone outline, portrait, centred on (cx, cy). The one shape the orientation cue rotates. */
function Phone({ cx, cy, w = 26, h = 44 }: { cx: number; cy: number; w?: number; h?: number }): JSX.Element {
  return (
    <g className="ac">
      <rect x={cx - w / 2} y={cy - h / 2} width={w} height={h} rx="4" {...S} strokeWidth="2.2" fill="none" />
      <path d={`M${cx - 4} ${cy - h / 2 + 4}h8`} {...S} strokeWidth="1.8" />
      <circle cx={cx} cy={cy + h / 2 - 4} r="1.6" fill="currentColor" />
    </g>
  );
}

/** A viewfinder frame — four corner brackets, the universal "this is what the camera sees". */
function Frame({ x, y, w, h }: { x: number; y: number; w: number; h: number }): JSX.Element {
  const c = Math.min(w, h) * 0.3;
  return (
    <g className="ac" {...S} strokeWidth="2.2" fill="none">
      <path d={`M${x} ${y + c}V${y}h${c}`} />
      <path d={`M${x + w - c} ${y}h${c}v${c}`} />
      <path d={`M${x + w} ${y + h - c}V${y + h}h${-c}`} />
      <path d={`M${x + c} ${y + h}h${-c}v${-c}`} />
    </g>
  );
}

function Street({ frame }: { frame: number | null }): JSX.Element {
  return (
    <>
      {/* one side of the street: a plain block with a blank board over a shuttered front */}
      <path d="M8 70V20h50v50" {...S} strokeWidth="2.2" fill="none" />
      <rect x="13" y="25" width="40" height="13" rx="1.5" {...S} strokeWidth="2" fill="none" />
      <path d="M16 48h20M16 55h14" {...S} strokeWidth="1.8" />
      <path d="M40 70V46h13v24" {...S} strokeWidth="1.8" fill="none" />
      {/* the street itself, and the far kerb the customer stands on */}
      <path d="M4 70h112" {...S} strokeWidth="2" />
      <path d="M10 79h100" {...S} strokeWidth="1.8" strokeDasharray="7 7" />
      <path d="M4 92h112" {...S} strokeWidth="2" />
      <Moving type="STREET" frame={frame}>
        <Person x={86} y={92} phone />
      </Moving>
    </>
  );
}

function Sideways({ frame }: { frame: number | null }): JSX.Element {
  return (
    <>
      {/* the turn, as an arc with a head: ink, so the phone keeps the single accent */}
      <path d="M31 22a38 38 0 0 1 58 0" {...S} strokeWidth="2" fill="none" strokeDasharray="5 5" />
      <path d="M84 14l6 8-9 3" {...S} strokeWidth="2" fill="none" />
      <Moving type="SIDEWAYS" frame={frame}>
        <Phone cx={60} cy={46} />
      </Moving>
      <path d="M26 82h68" {...S} strokeWidth="2" />
    </>
  );
}

function Door({ frame }: { frame: number | null }): JSX.Element {
  return (
    <>
      {/* the front of a building, with the way in */}
      <path d="M56 78V22h56v56" {...S} strokeWidth="2.2" fill="none" />
      <path d="M52 22h64" {...S} strokeWidth="2" />
      <g className="ac">
        <rect x="74" y="44" width="22" height="34" rx="1.5" {...S} strokeWidth="2.2" fill="none" />
        <circle cx="91" cy="62" r="1.8" fill="currentColor" />
      </g>
      <path d="M62 34h9M101 34h7" {...S} strokeWidth="1.8" />
      <path d="M4 78h112" {...S} strokeWidth="2" />
      {/* footsteps toward the door */}
      <ellipse cx="14" cy="84" rx="3.4" ry="2.1" {...S} strokeWidth="1.5" fill="none" />
      <ellipse cx="26" cy="87" rx="3.4" ry="2.1" {...S} strokeWidth="1.5" fill="none" />
      <ellipse cx="38" cy="84" rx="3.4" ry="2.1" {...S} strokeWidth="1.5" fill="none" />
      <Moving type="DOOR" frame={frame}>
        <Person x={30} y={78} phone />
      </Moving>
    </>
  );
}

function Turn({ frame }: { frame: number | null }): JSX.Element {
  return (
    <>
      {/* seen from above: the room's corners, the person in the middle, one slow sweep */}
      <path d="M18 16h84v60H18z" {...S} strokeWidth="1.8" fill="none" strokeDasharray="6 6" />
      <circle cx="60" cy="46" r="27" {...S} strokeWidth="1.8" fill="none" strokeDasharray="4 6" />
      <circle cx="60" cy="46" r="6" {...S} strokeWidth="2.2" fill="none" />
      <path d="M52 54a9 9 0 0 1 16 0" {...S} strokeWidth="2" fill="none" />
      <Moving type="TURN" frame={frame}>
        <g className="ac">
          <path d="M60 46L60 21" {...S} strokeWidth="2.2" />
          <path d="M55 27l5-6 5 6" {...S} strokeWidth="2.2" fill="none" />
        </g>
      </Moving>
    </>
  );
}

function Plate({ frame }: { frame: number | null }): JSX.Element {
  return (
    <>
      {/* a door with a small blank plate; the camera frame closes in on it */}
      <path d="M52 8v78h56V8z" {...S} strokeWidth="2.2" fill="none" />
      <circle cx="58" cy="50" r="2" fill="currentColor" />
      <rect x="66" y="24" width="30" height="14" rx="1.5" {...S} strokeWidth="2" fill="none" />
      <path d="M71 29h20M71 34h13" {...S} strokeWidth="1.6" />
      <path d="M8 86h104" {...S} strokeWidth="2" />
      <g transform="translate(81 31)">
        <Moving type="PLATE" frame={frame}>
          <Frame x={-40} y={-22} w={80} h={44} />
        </Moving>
      </g>
    </>
  );
}

function Certificates({ frame }: { frame: number | null }): JSX.Element {
  return (
    <>
      {/* a wall of blank framed sheets; the camera pans across them */}
      <path d="M6 20h108" {...S} strokeWidth="2" />
      <path d="M6 78h108" {...S} strokeWidth="2" />
      {[16, 48, 80].map((x) => (
        <g key={`c${x}`}>
          <rect x={x} y="30" width="24" height="30" rx="1.5" {...S} strokeWidth="2" fill="none" />
          <path d={`M${x + 5} 39h14M${x + 5} 45h14M${x + 5} 51h9`} {...S} strokeWidth="1.5" />
        </g>
      ))}
      <Moving type="CERTIFICATES" frame={frame}>
        <Frame x={40} y={24} w={40} h={42} />
      </Moving>
    </>
  );
}

function Face({ frame }: { frame: number | null }): JSX.Element {
  return (
    <>
      {/* the oval the round-1 selfie screen already draws, and a head that ends up straight on */}
      <g className="ac">
        <ellipse cx="60" cy="40" rx="21" ry="27" {...S} strokeWidth="2.2" fill="none" strokeDasharray="6 5" />
      </g>
      <Moving type="FACE" frame={frame}>
        <circle cx="60" cy="38" r="15" {...S} strokeWidth="2.2" fill="none" />
        <circle cx="54" cy="36" r="1.7" fill="currentColor" />
        <circle cx="66" cy="36" r="1.7" fill="currentColor" />
        <path d="M60 41v4" {...S} strokeWidth="1.8" />
        <path d="M56 48a6 6 0 0 0 8 0" {...S} strokeWidth="1.8" fill="none" />
        <path d="M40 80a20 20 0 0 1 40 0" {...S} strokeWidth="2.2" fill="none" />
      </Moving>
    </>
  );
}

function IdFlat({ frame }: { frame: number | null }): JSX.Element {
  return (
    <>
      {/* a hand holding a blank card; it settles flat. The light lines say "well lit", no lamp, no text */}
      <path d="M18 24l7 7M30 16v9M44 22l-5 6" {...S} strokeWidth="1.8" />
      <Moving type="ID_FLAT" frame={frame}>
        <g className="ac">
          <rect x="30" y="34" width="60" height="38" rx="3" {...S} strokeWidth="2.2" fill="none" />
        </g>
        <rect x="36" y="41" width="15" height="19" rx="1.5" {...S} strokeWidth="1.8" fill="none" />
        <circle cx="43.5" cy="47" r="3.2" {...S} strokeWidth="1.5" fill="none" />
        <path d="M38.4 57a5.4 5.4 0 0 1 10.2 0" {...S} strokeWidth="1.5" fill="none" />
        <path d="M57 44h26M57 51h26M57 58h17" {...S} strokeWidth="1.8" />
      </Moving>
      <path d="M26 74c4 8 12 12 22 12h28c8 0 12-4 12-9" {...S} strokeWidth="2.2" fill="none" />
      <path d="M34 78v8M44 80v6" {...S} strokeWidth="1.8" />
    </>
  );
}

const SCENES: Readonly<Record<DemoType, (p: { frame: number | null }) => JSX.Element>> = {
  STREET: Street,
  SIDEWAYS: Sideways,
  DOOR: Door,
  TURN: Turn,
  PLATE: Plate,
  CERTIFICATES: Certificates,
  FACE: Face,
  ID_FLAT: IdFlat,
};

/**
 * One demonstration. `frame === null` ⇒ the loop; `0 | 1 | 2` ⇒ that static frame (reduced motion, low data).
 * Decorative by construction: the caption beside it carries the meaning, so a screen reader is given the words,
 * never a description of pixels (§6.5's rule, applied to the illustrations).
 */
export function DemoArt({ type, frame = null, size = 168 }: { type: DemoType; frame?: number | null; size?: number }): JSX.Element {
  const Scene = SCENES[type];
  return (
    <svg
      className="sv-showme-art"
      viewBox="0 0 120 96"
      width={size}
      height={Math.round((size * 96) / 120)}
      fill="none"
      aria-hidden="true"
      role="presentation"
      data-showme={type}
      data-frame={frame === null ? 'motion' : String(frame)}
    >
      <Scene frame={frame} />
    </svg>
  );
}
